import { Link } from "react-router-dom";
import { BentoGrid, type BentoGridItem } from "@/components/wensity/bento-grid";
import { Button } from "@/components/wensity/button";
import { Badge } from "@/components/wensity/badge";
import { InfiniteMarquee } from "@/components/wensity/infinite-marquee";
import { ContentCard } from "@/components/ContentCard";
import { TerminalCard } from "@/components/TerminalCard";
import { SectionHeader, LinkTileRow } from "@/components/LinkCards";
import { useSiteContent } from "@/data/SiteContentContext";
import { getContentByKind } from "@/data/site";

const pillars: BentoGridItem[] = [
  {
    id: "journal",
    colSpan: 3,
    title: "Personal notes",
    description: "Short entries on family, tech, and what they look like together.",
    children: (
      <div className="flex h-full flex-col justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--color-primary)]">// journal</p>
          <h3 className="mt-3 text-xl font-semibold text-white">Personal notes</h3>
          <p className="mt-2 text-sm text-white/60">Stories worth keeping around.</p>
        </div>
        <span className="mt-6 text-[var(--color-primary)]">→</span>
      </div>
    ),
  },
  {
    id: "guides",
    colSpan: 3,
    title: "Real-world walkthroughs",
    description: "Procedures for work, family, and friends.",
    children: (
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--color-primary)]">// guides</p>
        <h3 className="mt-3 text-lg font-semibold text-white">Real-world walkthroughs</h3>
      </div>
    ),
  },
  {
    id: "scripts",
    colSpan: 3,
    title: "PowerShell that pulls its weight",
    description: "Copy-paste or grab the raw .ps1.",
    children: (
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--color-primary)]">// scripts</p>
        <h3 className="mt-3 text-lg font-semibold text-white">PowerShell that pulls its weight</h3>
      </div>
    ),
  },
];

const stackBadges = ["Apple", "Microsoft 365", "PowerShell", "Intune", "Cloudflare", "Tailscale"];

export function HomePage() {
  const { site, content, gearSections, appSections, serviceSections } = useSiteContent();
  const journal = getContentByKind(content, "journal").slice(0, 3);
  const guides = getContentByKind(content, "guide").slice(0, 3);
  const scripts = getContentByKind(content, "script").slice(0, 3);
  const gear = gearSections.flatMap((section) => section.items).slice(0, 8);
  const apps = appSections.flatMap((section) => section.items).slice(0, 8);
  const services = serviceSections.flatMap((section) => section.items).slice(0, 8);

  return (
    <div className="space-y-16">
      <section className="grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-6">
          <h1 className="max-w-2xl text-4xl font-semibold tracking-tight text-[var(--color-foreground)] sm:text-5xl">
            Tech bits, life bits, and the links that <span className="text-[var(--color-primary)]">spark</span> them.
          </h1>
          <p className="max-w-xl text-lg leading-8 text-[var(--color-muted)]">{site.author.bio}</p>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="brand">
              <Link to="/journal">Read the journal</Link>
            </Button>
            <Button asChild variant="outline" className="border-[var(--color-border)] bg-transparent text-[var(--color-foreground)]">
              <Link to="/scripts">Browse scripts</Link>
            </Button>
          </div>
        </div>

        <div className="space-y-5">
          <TerminalCard />
        </div>
      </section>

      <section>
        <InfiniteMarquee
          speed={24}
          items={stackBadges.map((label) => (
            <Badge key={label} variant="neutral" size="lg" radius="pill">
              {label}
            </Badge>
          ))}
        />
      </section>

      <section>
        <SectionHeader title="What lives here" />
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { href: "/journal", item: pillars[0] },
            { href: "/guides", item: pillars[1] },
            { href: "/scripts", item: pillars[2] },
          ].map(({ href, item }) => (
            <Link key={item.id} to={href} className="block">
              <BentoGrid items={[item]} columns={3} cellVariant="double-frame" className="h-full" />
            </Link>
          ))}
        </div>
      </section>

      <section>
        <SectionHeader title="From the journal" href="/journal" linkLabel="All entries →" />
        <div className="grid gap-4 md:grid-cols-3">{journal.map((item) => <ContentCard key={item.slug} item={item} />)}</div>
      </section>

      <section>
        <SectionHeader title="Guides" href="/guides" linkLabel="All guides →" />
        <div className="grid gap-4 md:grid-cols-3">{guides.map((item) => <ContentCard key={item.slug} item={item} />)}</div>
      </section>

      <section>
        <SectionHeader title="Scripts" href="/scripts" linkLabel="All scripts →" />
        <div className="grid gap-4 md:grid-cols-3">{scripts.map((item) => <ContentCard key={item.slug} item={item} />)}</div>
      </section>

      <section>
        <SectionHeader title="Gear" href="/gear" linkLabel="All gear →" />
        <LinkTileRow items={gear} />
      </section>

      <section>
        <SectionHeader title="Apps" href="/apps" linkLabel="All apps →" />
        <LinkTileRow items={apps} />
      </section>

      <section>
        <SectionHeader title="Services" href="/services" linkLabel="All services →" />
        <LinkTileRow items={services} />
      </section>
    </div>
  );
}
